import { type ReactNode } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useHorizontalCarousel } from '@/hooks/use-horizontal-carousel'

interface HorizontalCarouselProps {
	children: ReactNode
	className?: string
	contentClassName?: string
	controls?: 'overlay' | 'none'
}

export function HorizontalCarousel({ children, className, contentClassName, controls = 'overlay' }: HorizontalCarouselProps) {
	const { scrollRef, canScrollPrev, canScrollNext, handleScroll } = useHorizontalCarousel(children)

	return (
		<div className={cn('relative', className)}>
			{controls === 'overlay' && canScrollPrev ? (
				<button
					type='button'
					onClick={() => handleScroll('prev')}
					className='absolute -left-3 top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg border border-border bg-card text-foreground shadow-sm transition-opacity hover:bg-accent sm:flex'
					aria-label='Scroll previous'
				>
					<ChevronLeft className='h-4 w-4' />
				</button>
			) : null}

			<div
				ref={scrollRef}
				className={cn(
					'flex gap-6 overflow-x-auto overflow-y-visible scroll-smooth px-[5px] pb-8 pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
					contentClassName,
				)}
			>
				{children}
			</div>

			{controls === 'overlay' && canScrollNext ? (
				<button
					type='button'
					onClick={() => handleScroll('next')}
					className='absolute -right-3 top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg border border-border bg-card text-foreground shadow-sm transition-opacity hover:bg-accent sm:flex'
					aria-label='Scroll next'
				>
					<ChevronRight className='h-4 w-4' />
				</button>
			) : null}
		</div>
	)
}
